import React, { useState, useEffect, useCallback } from "react";
import { X, Loader2, Save, AlertCircle } from "lucide-react";
import { PORTFOLIO_CATEGORIES } from "@/data/portfolioCategories";
import AdminImageUploader from "@/components/admin/AdminImageUploader";

const BLANK = {
  title: "",
  category: "roofing",
  city: "",
  description: "",
  is_before_after: false,
  is_published: true,
  images: [],
};

/**
 * AdminProjectModal
 *
 * Create / edit modal for a portfolio project. Lets the admin pick a
 * category, write copy, toggle publish + before/after, and upload up to 8
 * images via AdminImageUploader. Calls `onSave(payload)` with the cleaned
 * project payload.
 */
export default function AdminProjectModal({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...BLANK, ...initial } : BLANK);
      setError("");
    }
  }, [open, initial]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const updateImages = useCallback((next) => {
    setForm((p) => ({ ...p, images: next }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        ...form,
        title: form.title.trim(),
        city: (form.city || "").trim(),
        description: (form.description || "").trim(),
        images: form.images.map((img, i) => ({
          ...img,
          sort_order: i,
          is_before: !!img.is_before,
          alt: img.alt || "",
        })),
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save project. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[150] bg-br-black/70 backdrop-blur-sm flex items-start justify-center px-3 py-6 sm:py-10 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="admin-project-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl my-auto">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
          <div>
            <div className="br-eyebrow text-br-bronze">Portfolio</div>
            <h3 className="font-display text-xl tracking-widest uppercase mt-1">
              {mode === "edit" ? "Edit Project" : "New Project"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-br-black" aria-label="Close" data-testid="project-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="project-modal-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label-br" htmlFor="proj-title">Title *</label>
              <input
                id="proj-title"
                className="input-br"
                value={form.title}
                onChange={update("title")}
                placeholder="e.g. Cedar Park 30-Year Roof Replacement"
                required
                data-testid="project-title"
              />
            </div>
            <div>
              <label className="label-br" htmlFor="proj-category">Category *</label>
              <select id="proj-category" className="select-br" value={form.category} onChange={update("category")} required data-testid="project-category">
                {PORTFOLIO_CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-br" htmlFor="proj-city">City / Area</label>
              <input id="proj-city" className="input-br" value={form.city} onChange={update("city")} placeholder="Austin, Round Rock, Cedar Park…" data-testid="project-city" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-br" htmlFor="proj-desc">Short Description</label>
              <textarea
                id="proj-desc"
                rows={3}
                className="textarea-br"
                value={form.description}
                onChange={update("description")}
                placeholder="Scope of work, materials, anything that helps the homeowner picture the result."
                maxLength={2000}
                data-testid="project-description"
              />
              <div className="mt-1 text-[11px] text-gray-400 text-right">{(form.description || "").length}/2000</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 border-y border-gray-100 py-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="accent-br-bronze h-4 w-4"
                checked={!!form.is_before_after}
                onChange={update("is_before_after")}
                data-testid="project-before-after"
              />
              Before / After project
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="accent-br-bronze h-4 w-4"
                checked={!!form.is_published}
                onChange={update("is_published")}
                data-testid="project-published"
              />
              Published on public portfolio
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-br mb-0">Project Images</div>
              <div className="text-[11px] text-gray-500">{form.images.length} / 8</div>
            </div>
            <AdminImageUploader
              images={form.images}
              onChange={updateImages}
              isBeforeAfter={!!form.is_before_after}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-start gap-2" data-testid="project-modal-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500" data-testid="project-modal-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="br-btn-primary" data-testid="project-modal-save">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : mode === "edit" ? "Save Changes" : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
